/**
 * @contract
 *   Назначение: Инлайн-форма отправки отзыва о ЧОПе на публичной странице /chop/[slug]
 *   Поля:       Рейтинг звёздами (1-5), текст отзыва (20-2000 символов), имя (опционально)
 *   Антиспам:   Honeypot-поле, скрытое от пользователей
 *   Статус:     После submit — «Спасибо, отзыв отправлен на модерацию»
 *   Автор:      AI Agent
 *   Дата:       2026-06-23
 */

"use client";

import React, { useState } from "react";
import { Star, Send, CheckCircle, AlertCircle, MessageSquare } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { useReviewSubmit, type SubmitState } from "@/features/seo-portal/hooks/use-review-submit";

interface ChopReviewFormProps {
  companyId: string;
}

/**
 * Инлайн-форма отзыва на странице ЧОПа.
 * Пользователь может оставить отзыв без авторизации. После отправки — сообщение об успехе.
 * Использует общий хук useReviewSubmit (единый API-контракт с add-review-dialog.tsx).
 */
export function ChopReviewForm({ companyId }: ChopReviewFormProps) {
  const { state, errorMessage, submit, reset: resetHook } = useReviewSubmit();

  // Поля формы
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [content, setContent] = useState("");
  const [authorName, setAuthorName] = useState("");

  // Honeypot — скрытое поле, которое заполняют только боты
  const [honeypot, setHoneypot] = useState("");

  // 152-ФЗ: явное согласие на обработку ПДн (имя автора)
  const [consent, setConsent] = useState(false);

  // Валидация: оценка обязательна, текст минимум 20 символов (проверяется хуком)
  const isValid = rating > 0 && content.trim().length >= 20 && consent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    await submit({
      chop_id: companyId,
      author_name: authorName.trim() || "Аноним",
      author_role: "client",
      rating,
      content: content.trim(),
      honeypot,
    });

    // 152-ФЗ: логируем согласие на обработку ПДн
    fetch("/api/pdpn/consent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: "chop_review",
        metadata: { chop_id: companyId, author_name: authorName.trim() || "Аноним" },
      }),
    }).catch(() => { /* silent — не блокируем отзыв */ });
  };

  // Сброс формы для повторной отправки
  const handleReset = () => {
    resetHook();
    setRating(0);
    setHoverRating(0);
    setContent("");
    setAuthorName("");
    setHoneypot("");
  };

  // Состояние: успешная отправка
  if (state === "success") {
    return (
      <div className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-accent-muted))] rounded-none p-6 md:p-8">
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-14 h-14 rounded-full bg-[hsl(var(--portal-accent-muted))] border border-[hsl(var(--portal-accent-muted))] flex items-center justify-center">
            <CheckCircle className="w-7 h-7 text-[hsl(var(--portal-accent))]" />
          </div>
          <div>
            <h3 className="font-black text-[hsl(var(--portal-text))] uppercase tracking-tight mb-1 text-lg">
              Спасибо за ваш отзыв!
            </h3>
            <p className="text-[hsl(var(--portal-text-muted))] text-sm font-sans max-w-md mx-auto">
              Отзыв отправлен на модерацию и появится на сайте после проверки
              (обычно в течение 24 часов).
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="border-[hsl(var(--portal-border-hover))] hover:bg-[hsl(var(--portal-surface-2))] text-[hsl(var(--portal-text))] rounded-none"
          >
            Написать ещё отзыв
          </Button>
        </div>
      </div>
    );
  }

  // Основная форма
  return (
    <div className="bg-[hsl(var(--portal-bg))] border border-[hsl(var(--portal-border-hover))] rounded-none p-6 md:p-8">
      <h3 className="font-black text-[hsl(var(--portal-text))] uppercase tracking-tight mb-5 flex items-center gap-2 text-base">
        <MessageSquare className="w-5 h-5 text-[hsl(var(--portal-text-muted))]" />
        Оставить отзыв о компании
      </h3>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot — скрыт от людей, видим для ботов */}
        {/* ponytail: ступень 2. Стандартный антиспам-паттерн. */}
        <input
          type="text"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute opacity-0 pointer-events-none w-0 h-0"
        />

        {/* Рейтинг звёздами */}
        <div className="space-y-1.5">
          <label className="text-[hsl(var(--portal-text-secondary))] text-xs uppercase tracking-widest font-bold">
            Оценка <span className="text-[hsl(var(--portal-text-muted))]">*</span>
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 rounded-none transition-transform hover:scale-110"
                aria-label={`Оценить на ${star} ${star === 1 ? "звезду" : star < 5 ? "звезды" : "звёзд"}`}
              >
                <Star
                  className={`w-8 h-8 transition-colors ${
                    star <= (hoverRating || rating)
                      ? "fill-[hsl(var(--portal-warn))] text-[hsl(var(--portal-warn))]"
                      : "fill-transparent text-[hsl(var(--portal-text-dim))]"
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-3 text-sm text-[hsl(var(--portal-text-muted))] font-sans">
                {["", "Плохо", "Так себе", "Нормально", "Хорошо", "Отлично"][rating]}
              </span>
            )}
          </div>
        </div>

        {/* Текст отзыва */}
        <div className="space-y-1.5">
          <label className="text-[hsl(var(--portal-text-secondary))] text-xs uppercase tracking-widest font-bold">
            Текст отзыва <span className="text-[hsl(var(--portal-text-muted))]">*</span>
            <span className="ml-2 text-[hsl(var(--portal-text-dim))] normal-case font-normal">
              (мин. 20 символов)
            </span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Поделитесь опытом работы или услугами этого ЧОПа..."
            required
            minLength={20}
            maxLength={2000}
            rows={4}
            className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] rounded-none p-3 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-[hsl(var(--portal-border-hover))] transition-colors"
          />
          <div className="flex justify-between items-center">
            {content.length > 0 && content.length < 20 && (
              <span className="text-[11px] text-[hsl(var(--portal-warn))] font-mono tabular-nums">
                Минимум 20 символов (осталось {20 - content.length})
              </span>
            )}
            <span className="text-[11px] text-[hsl(var(--portal-text-dim))] font-mono tabular-nums ml-auto">
              {content.length}/2000
            </span>
          </div>
        </div>

        {/* Имя (необязательное) */}
        <div className="space-y-1.5">
          <label className="text-[hsl(var(--portal-text-secondary))] text-xs uppercase tracking-widest font-bold">
            Ваше имя <span className="text-[hsl(var(--portal-text-dim))] font-normal normal-case">(необязательно)</span>
          </label>
          <input
            type="text"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Аноним"
            maxLength={100}
            className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] rounded-none p-2.5 text-sm font-sans focus:outline-none focus:ring-1 focus:ring-[hsl(var(--portal-border-hover))] transition-colors"
          />
        </div>

        {/* 152-ФЗ: чекбокс явного согласия на обработку ПДн */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 rounded-none border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] accent-[hsl(var(--portal-accent))] cursor-pointer"
          />
          <span className="text-[11px] text-[hsl(var(--portal-text-muted))] leading-relaxed font-sans group-hover:text-[hsl(var(--portal-text-secondary))] transition-colors">
            Я даю согласие на обработку персональных данных в соответствии с{" "}
            <a href="/privacy" target="_blank" className="underline text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text-secondary))] transition-colors">
              Политикой конфиденциальности
            </a>
          </span>
        </label>

        {/* Дисклеймер */}
        <p className="text-[10px] text-[hsl(var(--portal-text-dim))] font-sans text-center uppercase tracking-widest leading-relaxed">
          Отзыв пройдёт модерацию перед публикацией. Мы не публикуем
          оскорбления, спам и личные данные.
        </p>

        {/* Ошибка */}
        {state === "error" && errorMessage && (
          <div className="flex items-start gap-2 p-3 bg-[hsl(var(--portal-danger))]/10 border border-[hsl(var(--portal-danger))]/30 rounded-none">
            <AlertCircle className="w-4 h-4 text-[hsl(var(--portal-danger))] shrink-0 mt-0.5" />
            <p className="text-[hsl(var(--portal-danger))] text-sm font-sans">{errorMessage}</p>
          </div>
        )}

        {/* Кнопка отправки */}
        <Button
          type="submit"
          disabled={state === "submitting" || !isValid}
          className="w-full bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] rounded-none font-black uppercase tracking-wider text-xs gap-2 h-11 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          {state === "submitting" ? "Отправляем..." : "Отправить отзыв"}
        </Button>
      </form>
    </div>
  );
}
